import * as yup from 'yup';

import { MAX_IMAGE_FILE_SIZE, requiredString } from 'utils/constants';
import { isFile } from 'utils/helpers';
import { PHONE_NUMBER_REGEX } from 'utils/validators';
import { UserRoleEnum } from 'utils/enums';
import { hasPermission, PermissionEnum } from 'utils/permissions';

export const FormFields = {
  userImage: 'userImage',
  enabled: 'enabled',
  healthSystem: 'healthSystemId',
  autologout: 'autologout',
  title: 'title',
  roles: 'roles',
  analyticsServiceLines: 'analyticsServiceLines',
};

export const isAnalyticsUser = (roles) =>
  !!roles?.includes(UserRoleEnum.ANALYTICS);

export const isProviderHealthSystemUser = (roles) =>
  !!roles?.some((role) =>
    [
      UserRoleEnum.EEG,
      UserRoleEnum.PEDS_EEG,
      UserRoleEnum.TELE_NEURO,
      UserRoleEnum.NEURO,
    ].includes(role),
  );

export const needsAnalyticsHealthSystems = (roles) =>
  isAnalyticsUser(roles) &&
  !roles.includes(UserRoleEnum.CUSTOMER) &&
  !isProviderHealthSystemUser(roles);

export const isPhoneRequired = (roles) =>
  !roles?.every(
    (role) =>
      role === UserRoleEnum.ADMINISTRATOR || role === UserRoleEnum.CUSTOMER,
  );

export const schema = yup.object().shape({
  [FormFields.userImage]: yup
    .mixed()
    .test(
      'fileSize',
      `The uploaded file exceeds the maximum allowed size of ${
        MAX_IMAGE_FILE_SIZE / 1024 ** 2
      }MB`,
      (value) => {
        if (isFile(value)) {
          return value.size <= MAX_IMAGE_FILE_SIZE;
        }

        return true;
      },
    ),
  [FormFields.enabled]: yup.string().required(requiredString),
  firstName: yup
    .string()
    .trim()
    .required(requiredString)
    .max(100, ({ max }) => `First Name should not exceed ${max} characters`),
  lastName: yup
    .string()
    .trim()
    .required(requiredString)
    .max(100, ({ max }) => `Last Name should not exceed ${max} characters`),
  [FormFields.title]: yup.string().nullable().trim(),
  email: yup
    .string()
    .trim()
    .required(requiredString)
    .email('Invalid Email')
    .max(2048, ({ max }) => `Email should not exceed ${max} characters`),
  [FormFields.roles]: yup.array().of(yup.string()).required(requiredString),
  [FormFields.healthSystem]: yup.array().when(FormFields.roles, {
    is: (roles) => needsAnalyticsHealthSystems(roles),
    then: (scheme) => scheme.min(1, requiredString).required(requiredString),
    otherwise: (scheme) => scheme.nullable(),
  }),
  facilities: yup.array().when(FormFields.roles, {
    is: (roles) => needsAnalyticsHealthSystems(roles),
    then: (scheme) => scheme.min(1, requiredString).required(requiredString),
    otherwise: (scheme) => scheme.nullable(),
  }),
  [FormFields.analyticsServiceLines]: yup
    .array()
    .of(yup.string())
    .when(FormFields.roles, {
      is: (roles) => isAnalyticsUser(roles),
      then: () =>
        yup
          .array()
          .of(yup.string())
          .min(1, requiredString)
          .required(requiredString),
      otherwise: () => yup.array().of(yup.string()).nullable(),
    }),
  phoneNumber: yup
    .string()
    .nullable()
    .trim()
    .matches(PHONE_NUMBER_REGEX, 'Invalid phone number')
    .when(FormFields.roles, {
      is: (roles) => isPhoneRequired(roles),
      then: () =>
        yup.string().required(requiredString).typeError(requiredString),
    }),
  shitAdminId: yup.string(),
  npi: yup.string().nullable(),
  [FormFields.autologout]: yup.boolean(),
  inactivityLogOutThreshold: yup
    .number()
    .when(FormFields.autologout, ([autologout], scheme) => {
      if (!autologout) return yup.string().nullable();
      return scheme
        .typeError('Must be a number')
        .positive('Please enter a number greater then zero')
        .integer('Please enter a integer value')
        .required('Required');
    }),
});

export const enabledOptions = [
  { label: 'Enabled', value: '1' },
  { label: 'Disabled', value: '0' },
];

const buildPermissionsFromForm = (data) => {
  const perms = [];

  if (data.showAllConsultRequests === '1') {
    perms.push(PermissionEnum.ShowAllConsultRequests);
  }
  if (data.viewClosedConsultRequests === '1') {
    perms.push(PermissionEnum.ViewClosedConsultRequests);
  }
  if (data.viewAndEditSettings === '1') {
    perms.push(PermissionEnum.ViewAndEditSettings);
  }
  if (data.showFullPatientNames === '1') {
    perms.push(PermissionEnum.ShowFullPatientNames);
  }
  if (data.viewAdminConsultTable === '1') {
    perms.push(PermissionEnum.ViewAdminConsultTable);
  }
  if (data.createSharedMacros === '1') {
    perms.push(PermissionEnum.CreateSharedMacros);
  }
  if (data.hasPaocPermission === '1') {
    perms.push(PermissionEnum.HasPaocPermission);
  }

  return perms;
};

export const mapForm2Api = (data) => {
  const {
    showAllConsultRequests,
    viewClosedConsultRequests,
    viewAndEditSettings,
    showFullPatientNames,
    viewAdminConsultTable,
    createSharedMacros,
    hasPaocPermission,
    ...rest
  } = data;

  return {
    ...rest,
    [FormFields.title]: data.title || null,
    [FormFields.analyticsServiceLines]: isAnalyticsUser(data.roles)
      ? data.analyticsServiceLines ?? []
      : [],
    shiftAdminUserId: data.shiftAdminUserId
      ? Number(data.shiftAdminUserId)
      : null,
    enabled: Boolean(Number(data.enabled)),
    enableMfa: Boolean(data.enableMfa),
    permissions: buildPermissionsFromForm(data),
    numberOfCharactersToShowPatientNames: Number(
      data.numberOfCharactersToShowPatientNames,
    ),
    inactivityLogOutThreshold: data.inactivityLogOutThreshold
      ? Number(data.inactivityLogOutThreshold)
      : null,
  };
};

export const mapApi2Form = ({
  facilities,
  enabled,
  permissions,
  inactivityLogOutThreshold,
  enableMfa,
  analyticsServiceLines,
  ...rest
}) => ({
  ...rest,
  enabled: enabled ? '1' : '0',
  [FormFields.analyticsServiceLines]:
    analyticsServiceLines?.map(({ serviceLine }) => serviceLine) ?? [],
  [FormFields.autologout]: Boolean(inactivityLogOutThreshold),
  inactivityLogOutThreshold,
  enableMfa,
  showAllConsultRequests: hasPermission(permissions, PermissionEnum.ShowAllConsultRequests) ? '1' : '0',
  viewClosedConsultRequests: hasPermission(permissions, PermissionEnum.ViewClosedConsultRequests) ? '1' : '0',
  viewAndEditSettings: hasPermission(permissions, PermissionEnum.ViewAndEditSettings) ? '1' : '0',
  showFullPatientNames: hasPermission(permissions, PermissionEnum.ShowFullPatientNames) ? '1' : '0',
  viewAdminConsultTable: hasPermission(permissions, PermissionEnum.ViewAdminConsultTable) ? '1' : '0',
  createSharedMacros: hasPermission(permissions, PermissionEnum.CreateSharedMacros) ? '1' : '0',
  hasPaocPermission: hasPermission(permissions, PermissionEnum.HasPaocPermission) ? '1' : '0',
  [FormFields.healthSystem]:
    facilities?.reduce(
      (res, f) =>
        res.includes(f.healthSystemId) ? res : [...res, f.healthSystemId],
      [],
    ) || [],
  facilities: facilities?.map(({ facilityId }) => facilityId),
});
